import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users, ExternalLink, BadgePercent, Info, BookOpen } from 'lucide-react';
import { ViubuxConnectInfoModal } from './ViubuxConnectInfoModal';

export function ViubuxConnectSection({
  isOpen,
  onToggle,
  isActive,
  onActiveChange,
  catalogoActive = false,
  onCatalogoChange,
  catalogoPrice = 2248,
  showDiscountPerItem = false,
  onShowDiscountPerItemChange,
  formatPrice,
  currency,
  connectPrice = 5879
}) {
  const [showInfo, setShowInfo] = useState(false);
  // Show the net price ($4,997) to the user, not the inflated base
  const netPrice = Math.round(connectPrice * 0.85);
  return (
    <>
    <div className="border border-white/10 rounded-xl overflow-hidden bg-gradient-to-b from-[#1a1f50]/50 to-[#080c2d]/50">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00c2ff] to-[#003fdd] flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <span className="text-white font-semibold text-lg block">Inscripción a ViubuxConnect</span>
            {isActive && (
              <span className="text-xs font-bold text-green-400 flex items-center gap-1">
                <BadgePercent className="w-3 h-3" />
                {formatPrice(netPrice)} {currency} + 15% descuento activo
              </span>
            )}
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {isOpen && (
        <div className="p-4 pt-0 border-t border-white/5 space-y-4">
          <div className="bg-gradient-to-r from-[#00c2ff]/10 to-[#003fdd]/10 border border-[#00c2ff]/20 rounded-lg p-4">
            <p className="text-white text-sm mb-2">
              Conoce más en{' '}
              <a 
                href="https://Connect.Viubux.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#00c2ff] hover:underline inline-flex items-center gap-1 font-semibold"
              >
                Connect.Viubux.com
                <ExternalLink className="w-3 h-3" />
              </a>
            </p>
            <p className="text-slate-400 text-sm">
              Destapa automáticamente un <span className="text-green-400 font-bold">15% de descuento</span> en todos nuestros servicios y forma parte de una comunidad activa.
            </p>
            <p className="text-white font-semibold text-sm mt-2">
              Inscripción: {formatPrice(netPrice)} {currency}
            </p>
            <button
              onClick={() => setShowInfo(true)}
              className="mt-3 inline-flex items-center gap-1.5 text-[#00c2ff] hover:text-white text-sm font-medium transition-colors"
              data-testid="btn-connect-ver-mas"
            >
              <Info className="w-3.5 h-3.5" />
              Ver más...
            </button>
          </div>

          {/* Catálogo 12 meses */}
          <button
            onClick={() => onCatalogoChange && onCatalogoChange(!catalogoActive)}
            className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
              catalogoActive
                ? 'border-[#00c2ff]/50 bg-[#00c2ff]/10 ring-1 ring-[#00c2ff]/20'
                : 'border-white/10 bg-white/[0.03] hover:border-white/20'
            }`}
            data-testid="btn-vc-catalogo"
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                catalogoActive ? 'border-[#00c2ff] bg-[#00c2ff]' : 'border-slate-500'
              }`}>
                {catalogoActive && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div>
                <p className={`text-sm font-medium ${catalogoActive ? 'text-[#00c2ff]' : 'text-white'}`}>
                  12 meses de Acceso al Catálogo de Viubux Connect
                </p>
                <p className="text-xs text-slate-500">Lee, escucha y aprende de audiolibros, ebooks, podcasts y más</p>
              </div>
            </div>
            <span className={`font-bold text-sm flex-shrink-0 ml-3 ${catalogoActive ? 'text-[#00c2ff]' : 'text-white'}`}>
              +{formatPrice(catalogoPrice)} {currency}
            </span>
          </button>

          {/* Toggle descuento por item - only when active */}
          {isActive && (
            <button
              onClick={() => onShowDiscountPerItemChange && onShowDiscountPerItemChange(!showDiscountPerItem)}
              className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                showDiscountPerItem
                  ? 'border-green-500/50 bg-green-500/10 ring-1 ring-green-500/20'
                  : 'border-white/10 bg-white/[0.03] hover:border-white/20'
              }`}
              data-testid="btn-vc-discount-per-item"
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  showDiscountPerItem ? 'border-green-500 bg-green-500' : 'border-slate-500'
                }`}>
                  {showDiscountPerItem && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${showDiscountPerItem ? 'text-green-400' : 'text-white'}`}>
                    Mostrar descuento por item en cotización
                  </p>
                  <p className="text-xs text-slate-500">Muestra el precio original tachado y el precio con 15% de descuento</p>
                </div>
              </div>
            </button>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => onActiveChange(false)}
              className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-all ${
                !isActive
                  ? 'bg-white/10 border-white/30 text-white'
                  : 'border-white/10 text-slate-500 hover:border-white/20 hover:text-slate-300'
              }`}
              data-testid="btn-connect-no"
            >
              No, gracias
            </button>
            <button
              onClick={() => onActiveChange(true)}
              className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-[#00c2ff]/20 to-[#003fdd]/20 border-[#00c2ff]/50 text-[#00c2ff] ring-1 ring-[#00c2ff]/30'
                  : 'border-white/10 text-slate-500 hover:border-[#00c2ff]/30 hover:text-slate-300'
              }`}
              data-testid="btn-connect-yes"
            >
              <div className="flex items-center justify-center gap-2">
                <BadgePercent className="w-4 h-4" />
                Inscribirme
              </div>
              <p className="text-xs mt-1 opacity-70">{formatPrice(netPrice)} + 15% desc.</p>
            </button>
          </div>
        </div>
      )}
    </div>
    <ViubuxConnectInfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
    </>
  );
}

export default ViubuxConnectSection;
